import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProductionOrder } from '../../types';
import { useAuth, authFetch } from '../../context/AuthContext';
import CategoryManager, { Category } from '../../components/ui/CategoryManager';

interface ApiProductionOrder {
  id: number | string;
  product_name: string;
  category: string | null;
  quantity: number | string;
  request_date: string;
  due_date: string;
  status: ProductionOrder['status'];
  partner_name: string;
  note: string | null;
}

interface Partner {
  id: number;
  code: string;
  name: string;
}

const emptyForm = () => ({
  productName: '',
  category: '',
  quantity: '',
  requestDate: new Date().toISOString().slice(0, 10),
  dueDate: '',
  partnerId: '',
  note: '',
});

const toProductionOrder = (order: ApiProductionOrder): ProductionOrder => ({
  id: Number(order.id),
  productName: order.product_name,
  category: order.category ?? undefined,
  quantity: Number(order.quantity),
  requestDate: order.request_date,
  dueDate: order.due_date,
  status: order.status,
  partner: order.partner_name,
  note: order.note ?? undefined,
});

const readResponse = async (response: Response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error ?? '요청을 처리하지 못했습니다.');
  return data;
};

const STATUS_FLOW = ['pending', 'preparing', 'producing', 'inspection', 'completed'];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: '접수', color: 'bg-stone-100 text-stone-600' },
  preparing: { label: '원자재 준비', color: 'bg-blue-100 text-blue-700' },
  producing: { label: '생산 중', color: 'bg-amber-100 text-amber-700' },
  inspection: { label: '검수 중', color: 'bg-purple-100 text-purple-700' },
  completed: { label: '완료', color: 'bg-green-100 text-green-700' },
  delayed: { label: '지연', color: 'bg-red-100 text-red-700' },
};

export default function ProductionPage() {
  const { can }    = useAuth();
  const location   = useLocation();
  const navigate   = useNavigate();
  const [orders, setOrders]         = useState<ProductionOrder[]>([]);
  const [showForm, setShowForm]     = useState(false);
  const [filterStatus, setFilterStatus] = useState('전체');
  const [form, setForm]             = useState(emptyForm);
  const [reactivateId, setReactivateId]         = useState<number | null>(null);
  const [reactivateStatus, setReactivateStatus] = useState<string>('producing');
  const [categories,   setCategories]           = useState<Category[]>([]);
  const [partners, setPartners]                 = useState<Partner[]>([]);
  const [showCatMgr,   setShowCatMgr]           = useState(false);
  const [loading, setLoading]                   = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [actionId, setActionId]                 = useState<number | null>(null);
  const [error, setError]                       = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ordersResponse, categoriesResponse, partnersResponse] = await Promise.all([
        authFetch('/api/production'),
        authFetch('/api/categories?active=1'),
        authFetch('/api/users/partners'),
      ]);
      const [orderData, categoryData, partnerData] = await Promise.all([
        readResponse(ordersResponse),
        readResponse(categoriesResponse),
        readResponse(partnersResponse),
      ]);
      setOrders((orderData as ApiProductionOrder[]).map(toProductionOrder));
      setCategories(categoryData as Category[]);
      setPartners(partnerData as Partner[]);
      setForm(current => ({
        ...current,
        partnerId: current.partnerId || String((partnerData as Partner[])[0]?.id ?? ''),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : '생산 현황을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  /* URL 파라미터로 모달 자동 오픈 */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'new') {
      setShowForm(true);
      navigate('/admin/production', { replace: true });
    }
    if (params.get('filter') === 'active') {
      setFilterStatus('producing');
      navigate('/admin/production', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateOrder = (updated: ApiProductionOrder) => {
    const mapped = toProductionOrder(updated);
    setOrders(current => current.map(order => order.id === mapped.id ? mapped : order));
  };

  const runOrderAction = async (id: number, action: 'advance' | 'delay') => {
    setActionId(id);
    setError('');
    try {
      const response = await authFetch(`/api/production/${id}/${action}`, { method: 'PATCH' });
      updateOrder(await readResponse(response) as ApiProductionOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : '상태를 변경하지 못했습니다.');
    } finally {
      setActionId(null);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateId) return;
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`/api/production/${reactivateId}/reactivate`, {
        method: 'PATCH',
        body: JSON.stringify({ status: reactivateStatus }),
      });
      updateOrder(await readResponse(response) as ApiProductionOrder);
      setReactivateId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '지연 상태를 해제하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await authFetch('/api/production', {
        method: 'POST',
        body: JSON.stringify({
          product_name: form.productName.trim(),
          category: form.category || null,
          quantity: Number(form.quantity),
          request_date: form.requestDate,
          due_date: form.dueDate,
          partner_id: Number(form.partnerId),
          note: form.note.trim() || null,
        }),
      });
      const created = toProductionOrder(await readResponse(response) as ApiProductionOrder);
      setOrders(current => [created, ...current]);
      setForm({ ...emptyForm(), partnerId: String(partners[0]?.id ?? '') });
      setShowForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '생산 주문을 등록하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const filtered = filterStatus === '전체' ? orders : orders.filter(o => o.status === filterStatus);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">생산 현황 관리</h1>
          <p className="text-stone-500 text-sm mt-1">생산 주문의 단계별 진행 현황 추적</p>
        </div>
        {can('production_create') && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition"
          >
            + 생산 주문 등록
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {/* 요약 카드 */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {Object.entries(STATUS_LABELS).map(([key, val]) => (
          <div key={key} className={`rounded-xl p-3 text-center ${val.color}`}>
            <p className="text-2xl font-bold">{orders.filter(o => o.status === key).length}</p>
            <p className="text-xs mt-0.5">{val.label}</p>
          </div>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['전체', ...Object.keys(STATUS_LABELS)].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-4 py-1.5 rounded-full text-sm transition ${
              filterStatus === s ? 'bg-amber-500 text-white' : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
            }`}
          >
            {s === '전체' ? '전체' : STATUS_LABELS[s].label}
          </button>
        ))}
      </div>

      {/* 생산 카드 목록 */}
      <div className="space-y-3">
        {loading && <p className="py-10 text-center text-sm text-stone-400">생산 현황을 불러오는 중입니다.</p>}
        {!loading && filtered.length === 0 && <p className="py-10 text-center text-sm text-stone-400">표시할 생산 주문이 없습니다.</p>}
        {filtered.map(order => {
          const isDelayed = order.status === 'delayed';
          const isDone = order.status === 'completed';
          return (
            <div key={order.id} className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${
              isDelayed ? 'border-red-400' : isDone ? 'border-green-400' : 'border-amber-400'
            }`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-bold text-stone-900">{order.productName}</h3>
                    {order.category && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                        {order.category}
                      </span>
                    )}
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_LABELS[order.status].color}`}>
                      {STATUS_LABELS[order.status].label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                    <span>수량: <strong className="text-stone-700">{order.quantity}개</strong></span>
                    <span>요청일: {order.requestDate}</span>
                    <span>납기일: <strong className={isDelayed ? 'text-red-600' : 'text-stone-700'}>{order.dueDate}</strong></span>
                    <span>담당: {order.partner}</span>
                    {isDone && (
                      <span className="text-green-600 font-semibold text-xs">
                        재고 자동 반영
                      </span>
                    )}
                  </div>
                  {order.note && (
                    <p className="text-xs text-stone-400 mt-1">📝 {order.note}</p>
                  )}

                  {/* 진행 바 */}
                  {!isDelayed && (
                    <div className="flex items-center gap-1 mt-3">
                      {STATUS_FLOW.map((s, i) => {
                        const currentIdx = STATUS_FLOW.indexOf(order.status);
                        return (
                          <React.Fragment key={s}>
                            <div className={`w-2 h-2 rounded-full ${i <= currentIdx ? 'bg-amber-500' : 'bg-stone-200'}`} />
                            {i < STATUS_FLOW.length - 1 && (
                              <div className={`h-0.5 flex-1 ${i < currentIdx ? 'bg-amber-500' : 'bg-stone-200'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  {/* 정상 진행 버튼 */}
                  {!isDone && !isDelayed && can('production_advance') && (
                    <button
                      onClick={() => runOrderAction(order.id, 'advance')}
                      disabled={actionId === order.id}
                      className="bg-amber-500 hover:bg-amber-600 text-black text-xs font-bold px-3 py-1.5 rounded-lg transition whitespace-nowrap"
                    >
                      다음 단계 →
                    </button>
                  )}
                  {/* 지연 처리 버튼 */}
                  {!isDone && !isDelayed && can('production_delay') && (
                    <button
                      onClick={() => runOrderAction(order.id, 'delay')}
                      disabled={actionId === order.id}
                      className="bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold px-3 py-1.5 rounded-lg transition whitespace-nowrap"
                    >
                      지연 처리
                    </button>
                  )}
                  {/* 지연 → 정상 복구 버튼 */}
                  {isDelayed && can('production_advance') && (
                    <button
                      onClick={() => { setReactivateId(order.id); setReactivateStatus('producing'); }}
                      className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition whitespace-nowrap"
                    >
                      ↩ 지연 해제
                    </button>
                  )}
                  {!can('production_advance') && !isDone && (
                    <span className="text-xs text-stone-400 text-right">관리자 승인 필요</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 등록 모달 */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="font-bold text-lg mb-4">생산 주문 등록</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">제품명 *</label>
                  <input
                    type="text"
                    value={form.productName}
                    onChange={e => setForm({ ...form, productName: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="예: 클래식 토트백 (블랙)"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-stone-700">카테고리</label>
                    <button
                      type="button"
                      onClick={() => setShowCatMgr(true)}
                      className="text-xs text-amber-600 hover:text-amber-700 font-semibold hover:underline"
                    >
                      관리
                    </button>
                  </div>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <option value="">-- 선택 --</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">생산 수량 *</label>
                <input
                  type="number"
                  value={form.quantity}
                  onChange={e => setForm({ ...form, quantity: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="수량 입력"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">담당 파트너 *</label>
                <select
                  value={form.partnerId}
                  onChange={e => setForm({ ...form, partnerId: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  required
                >
                  <option value="">-- 선택 --</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">요청일</label>
                  <input
                    type="date"
                    value={form.requestDate}
                    onChange={e => setForm({ ...form, requestDate: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">납기일</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={e => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">비고</label>
                <input
                  type="text"
                  value={form.note}
                  onChange={e => setForm({ ...form, note: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="비고사항 (선택)"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 border border-stone-300 text-stone-600 py-2.5 rounded-lg hover:bg-stone-50 transition text-sm"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2.5 rounded-lg transition text-sm"
                >
                  {saving ? '등록 중...' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── 지연 해제 모달 ── */}
      {reactivateId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="font-bold text-lg text-stone-900 mb-1">지연 해제</h2>
            <p className="text-stone-500 text-sm mb-5">복구할 생산 단계를 선택하세요.</p>

            <div className="space-y-2 mb-6">
              {[
                { value: 'pending',    label: '접수',        color: 'bg-stone-100 text-stone-600' },
                { value: 'preparing',  label: '원자재 준비', color: 'bg-blue-100 text-blue-700' },
                { value: 'producing',  label: '생산 중',     color: 'bg-amber-100 text-amber-700' },
                { value: 'inspection', label: '검수 중',     color: 'bg-purple-100 text-purple-700' },
              ].map(s => (
                <label key={s.value}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                    reactivateStatus === s.value
                      ? 'border-green-400 bg-green-50'
                      : 'border-stone-200 hover:bg-stone-50'
                  }`}>
                  <input
                    type="radio"
                    name="reactivate"
                    value={s.value}
                    checked={reactivateStatus === s.value}
                    onChange={() => setReactivateStatus(s.value)}
                    className="accent-green-500"
                  />
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${s.color}`}>
                    {s.label}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setReactivateId(null)}
                className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition"
              >
                취소
              </button>
              <button
                onClick={handleReactivate}
                disabled={saving}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-lg text-sm transition"
              >
                {saving ? '복구 중...' : '복구'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 카테고리 관리 모달 ── */}
      {showCatMgr && (
        <CategoryManager
          onClose={() => setShowCatMgr(false)}
          onSaved={setCategories}
        />
      )}
    </div>
  );
}
